import { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, BriefcaseBusiness, Globe2, Sparkles, X } from 'lucide-react';
import './VibeStoryTour.css';

const SCENES = [
  {
    icon: Sparkles,
    kicker: 'PLAY FIRST',
    title: 'TURN ANY SENTENCE INTO THE BIT.',
    intro: 'Pick a mode, type something ordinary, and make it screenshot-worthy.',
    sourceLabel: 'YOU TYPE',
    source: '“We are almost ready to launch.”',
    resultLabel: 'FOUNDER MODE',
    result: '“We’re pre-launch by design. The category is still catching up.”',
    note: 'Play gets you in fast. Switch to Translate when you need a real read on someone.',
  },
  {
    icon: BriefcaseBusiness,
    kicker: 'BUSINESS MODE',
    title: 'BUSINESS TALK, MADE ACTIONABLE.',
    intro: 'Turn vague feedback into the next thing you actually need to do.',
    sourceLabel: 'THEY SAID',
    source: '“Can you make this more strategic before we send it?”',
    resultLabel: 'LIKELY BRIEF',
    result: '“Add the decision, business impact, and recommendation—not just the details.”',
    note: 'A useful read, not certainty. Add the person and situation so Vibe can make the brief more specific.',
  },
  {
    icon: Globe2,
    kicker: 'CULTURE CHANGES CONTEXT',
    title: 'THE SAME WORDS LAND DIFFERENTLY.',
    intro: 'Add cultural context when directness, politeness, or disagreement works differently.',
    sourceLabel: 'CHINESE BUSINESS CONTEXT',
    source: '“That may be a little difficult.”',
    resultLabel: 'U.S. PLAIN ENGLISH',
    result: '“This is probably a no unless the plan changes.”',
    note: 'Culture is context, not a stereotype. You provide what matters for this person.',
  },
  {
    icon: Sparkles,
    kicker: 'HOW VIBE WORKS',
    title: 'PERSON. MESSAGE. MEANING. NEXT MOVE.',
    intro: 'Give us the context you know. We give you a likely read and replies you can actually send.',
    flow: [
      ['1', 'Choose who said it'],
      ['2', 'Paste the exact message'],
      ['3', 'Read the likely meaning'],
      ['4', 'Pick your best reply'],
    ],
    note: 'That’s it. Less guessing, fewer expensive misunderstandings.',
  },
];

export default function VibeStoryTour({ onClose }) {
  const [step, setStep] = useState(0);
  const [revealPhase, setRevealPhase] = useState('hidden');
  const scene = SCENES[step];
  const Icon = scene.icon;
  const last = step === SCENES.length - 1;

  useEffect(() => {
    if (scene.flow) return undefined;
    setRevealPhase('hidden');
    const sourceTimer = window.setTimeout(() => setRevealPhase('source'), 80);
    const arrowTimer = window.setTimeout(() => setRevealPhase('arrow'), 560);
    const resultTimer = window.setTimeout(() => setRevealPhase('result'), 1_020);
    return () => { window.clearTimeout(sourceTimer); window.clearTimeout(arrowTimer); window.clearTimeout(resultTimer); };
  }, [step, scene.flow]);

  const move = (next) => {
    setRevealPhase('hidden');
    setStep(next);
  };

  return (
    <div className="story-tour-backdrop" role="presentation">
      <section className="story-tour" role="dialog" aria-modal="true" aria-labelledby="story-tour-title">
        <button type="button" className="story-tour-close" onClick={onClose} aria-label="Close tutorial"><X /></button>

        <div className="story-tour-stage">
          <div className="story-tour-copy">
            <div className="story-tour-kicker"><Icon aria-hidden="true" /> {scene.kicker}</div>
            <h2 id="story-tour-title">{scene.title}</h2>
            <p>{scene.intro}</p>
          </div>

          {scene.flow ? (
            <div className="story-tour-flow">
              {scene.flow.map(([number, label]) => (
                <div key={number}><b>{number}</b><span>{label}</span></div>
              ))}
            </div>
          ) : (
            <div className={`story-tour-conversation is-${revealPhase}`} aria-live="polite">
              <div className="story-bubble story-bubble-source">
                <span>{scene.sourceLabel}</span>
                <p>{scene.source}</p>
              </div>
              <ArrowRight className="story-tour-arrow" aria-hidden="true" />
              <div className="story-bubble story-bubble-result">
                <span>{scene.resultLabel}</span>
                <p>{scene.result}</p>
              </div>
            </div>
          )}

          <p className="story-tour-note">{scene.note}</p>
        </div>

        <footer className="story-tour-controls">
          <div className="story-tour-progress" aria-label={`Tutorial page ${step + 1} of ${SCENES.length}`}>
            {SCENES.map((item, index) => <span key={item.kicker} className={index === step ? 'is-active' : ''} />)}
          </div>
          <div>
            {step > 0 && (
              <button type="button" className="story-tour-back" onClick={() => move(step - 1)}>
                <ArrowLeft aria-hidden="true" /> BACK
              </button>
            )}
            <button type="button" className="story-tour-next" onClick={() => last ? onClose() : move(step + 1)}>
              {last ? 'EXPLORE VIBE' : 'SHOW ME MORE'} {!last && <ArrowRight aria-hidden="true" />}
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
}
